import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, Wallet, ArrowRight, Truck } from "lucide-react";

export default function Checkout() {
  const { items, subtotal, discount, clear, coupon } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "West Bengal",
    pincode: "",
    notes: "",
    gift_note: "",
    payment_method: "cod",
  });

  const set = (k, v) => setForm({ ...form, [k]: v });

  // Fetch shipping quote when pincode or items change
  useEffect(() => {
    if (!/^\d{6}$/.test(form.pincode) || items.length === 0) {
      setQuote(null);
      return;
    }
    const itemStr = items.map(i => `${i.product_id}:${i.quantity}`).join(",");
    const discountedSub = Math.max(0, subtotal - discount);
    api.get("/shipping-quote", {
      params: { pincode: form.pincode, subtotal: discountedSub, item_ids: itemStr }
    }).then(({ data }) => setQuote(data)).catch(() => setQuote(null));
  }, [form.pincode, items, subtotal, discount]);

  const dynamicShipping = quote ? quote.shipping : (Math.max(0, subtotal - discount) >= 699 ? 0 : 60);
  const dynamicTotal = Math.max(0, subtotal - discount) + dynamicShipping;

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        ...form,
        coupon_code: coupon?.code || null,
        items: items.map(i => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          image: i.image,
          details: i.details || null,
        })),
      });
      clear();
      if (form.payment_method === "whatsapp") {
        // open whatsapp with order details
        try {
          const { data: cfg } = await api.get("/config");
          const wa = cfg.whatsapp.replace(/[^0-9]/g, "");
          const summary = items.map(i => `• ${i.name} × ${i.quantity} — ₹${i.price * i.quantity}`).join("\n");
          const msg = encodeURIComponent(
            `Hi Ave_luxe_candles!\n\nOrder ${data.order_number}\n\n${summary}\n\nSubtotal: ₹${data.subtotal}\nShipping: ₹${data.shipping}\nTotal: ₹${data.total}\n\nName: ${form.customer_name}\nPhone: ${form.phone}\nAddress: ${form.address}, ${form.city}, ${form.state} ${form.pincode}\n\nPlease confirm.`
          );
          window.open(`https://wa.me/${wa}?text=${msg}`, "_blank");
        } catch (err) { console.error("WhatsApp share failed", err); }
      }
      toast.success("Order placed! We'll be in touch shortly.");
      navigate(`/order-success/${data.order_number}`);
    } catch (err) {
      console.error("Order create failed", err);
      toast.error("Could not place order. Please try again.");
    }
    setSubmitting(false);
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h2 className="font-serif-display text-4xl mb-4">Your cart is empty</h2>
        <Link to="/shop" className="text-sm underline underline-offset-8 hover:text-[#D27D60]">Continue shopping</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="checkout-page">
      <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">✦ Checkout</div>
      <h1 className="font-serif-display text-5xl md:text-6xl leading-tight mb-12">A few final details</h1>

      <form onSubmit={submit} className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-6">
          <Section title="Contact">
            <Grid>
              <Field label="Full name" required value={form.customer_name} onChange={(v) => set("customer_name", v)} testid="checkout-name" />
              <Field label="Phone" required value={form.phone} onChange={(v) => set("phone", v)} testid="checkout-phone" />
            </Grid>
            <Field label="Email" type="email" required value={form.email} onChange={(v) => set("email", v)} testid="checkout-email" />
          </Section>

          <Section title="Delivery address">
            <Field label="Address" required value={form.address} onChange={(v) => set("address", v)} testid="checkout-address" />
            <Grid>
              <Field label="City" required value={form.city} onChange={(v) => set("city", v)} testid="checkout-city" />
              <Field label="State" required value={form.state} onChange={(v) => set("state", v)} testid="checkout-state" />
              <Field label="Pincode" required value={form.pincode} onChange={(v) => set("pincode", v)} testid="checkout-pincode" />
            </Grid>
            <Field label="Delivery notes (optional)" value={form.notes} onChange={(v) => set("notes", v)} textarea testid="checkout-notes" />
          </Section>

          <Section title="Gift note (optional)">
            <div className="p-4 rounded-lg bg-[#F3EFEA]/60 border border-[#E3DCD2] flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#D27D60]/15 text-[#D27D60] flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-lg">✎</span>
              </div>
              <div className="flex-1">
                <div className="text-xs text-[#4a4a4a] mb-3">
                  Sending this as a gift? Write a short message — we'll hand-print it on a card and tuck it inside your order.
                </div>
                <textarea
                  rows={3}
                  maxLength={300}
                  placeholder="Dear Anaya, wishing you the softest year ahead. Love, R."
                  value={form.gift_note}
                  onChange={(e) => set("gift_note", e.target.value)}
                  className="w-full bg-[#FDFBF7] border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors resize-none font-serif-display italic"
                  data-testid="checkout-gift-note"
                />
                <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mt-1 text-right">
                  {form.gift_note.length}/300 · complimentary
                </div>
              </div>
            </div>
          </Section>

          <Section title="Payment method">
            <div className="grid md:grid-cols-2 gap-3">
              <PayOption
                selected={form.payment_method === "cod"}
                onSelect={() => set("payment_method", "cod")}
                icon={Wallet}
                title="Cash on Delivery"
                sub="Pay when your order arrives"
                testid="checkout-payment-cod"
              />
              <PayOption
                selected={form.payment_method === "whatsapp"}
                onSelect={() => set("payment_method", "whatsapp")}
                icon={MessageCircle}
                title="Order via WhatsApp"
                sub="We'll confirm & share payment"
                testid="checkout-payment-whatsapp"
              />
            </div>
          </Section>
        </div>

        <div className="lg:col-span-5">
          <div className="p-8 bg-[#F3EFEA]/60 rounded-lg sticky top-32">
            <div className="text-[11px] uppercase tracking-widest-plus text-[#9BA595] mb-6">Order summary</div>
            <div className="space-y-4 mb-6">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-3 text-sm">
                  <div className="w-14 h-14 rounded overflow-hidden bg-[#FDFBF7] relative shrink-0">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                    <span className="absolute -top-1 -right-1 bg-[#1A1A1A] text-[#FDFBF7] text-[10px] rounded-full w-5 h-5 flex items-center justify-center">{i.quantity}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{i.name}</div>
                    <div className="text-xs text-[#9BA595]">₹{i.price} × {i.quantity}</div>
                  </div>
                  <div className="text-sm font-medium">₹{i.price * i.quantity}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#E3DCD2] pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-[#D27D60]"><span>Discount ({coupon?.code})</span><span>-₹{discount.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between">
                <span className="flex items-center gap-1.5">
                  <Truck className="w-3 h-3" strokeWidth={1.5} />
                  Shipping{quote && <span className="text-[10px] uppercase tracking-widest-plus text-[#9BA595]">· {quote.zone_label}</span>}
                </span>
                <span>{quote ? (dynamicShipping === 0 ? "Free" : `₹${dynamicShipping}`) : "Enter pincode"}</span>
              </div>
            </div>
            <div className="flex justify-between items-baseline mt-4 pt-4 border-t border-[#E3DCD2] mb-8">
              <span className="text-sm uppercase tracking-widest-plus">Total</span>
              <span className="font-serif-display text-3xl">₹{dynamicTotal.toFixed(2)}</span>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1A1A1A] text-[#FDFBF7] px-6 py-3.5 rounded-full text-sm uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
              data-testid="checkout-place-order-btn"
            >
              {submitting ? "Placing order..." : "Place order"}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <p className="mt-4 text-[10px] uppercase tracking-widest-plus text-center text-[#9BA595]">
              By placing this order you agree to our terms
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-4">
      <div className="text-xs uppercase tracking-widest-plus text-[#9BA595]">{title}</div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, value, onChange, type = "text", required, textarea, testid }) {
  const cls = "w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors";
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] mb-1.5">{label}{required && " *"}</div>
      {textarea ? (
        <textarea
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${cls} resize-none`}
          data-testid={testid}
        />
      ) : (
        <input
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
          data-testid={testid}
        />
      )}
    </label>
  );
}

function PayOption({ selected, onSelect, icon: Icon, title, sub, testid }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left p-4 rounded-lg border-2 transition-colors ${
        selected ? "border-[#D27D60] bg-[#FDFBF7]" : "border-[#E3DCD2] hover:border-[#9BA595]"
      }`}
      data-testid={testid}
    >
      <Icon className={`w-5 h-5 mb-2 ${selected ? "text-[#D27D60]" : "text-[#9BA595]"}`} strokeWidth={1.5} />
      <div className="text-sm font-medium">{title}</div>
      <div className="text-xs text-[#9BA595]">{sub}</div>
    </button>
  );
}
